import React, { useEffect, useMemo } from 'react';
import { Box, Flex } from 'grid-styled';
import { Price } from '@/types';
import Field from '@/components/Field';
import Counter from '@/components/Counter';
import { FormProps } from '@/components/Form';
import { DateQuantityProps, cx } from './DateQuantities';
import useTranslate from '@/hooks/useTranslate';

interface DateQuantityFormProps extends DateQuantityProps, FormProps<{ price: number; quantity: number; }> {
    onIncrement: (val: number, cb: () => void) => void;
    onDecrement: (val: number, cb: () => void) => void;
}

export const DateQuantityForm = React.memo<DateQuantityFormProps>(({
    service,
    formValues,
    change,
    prices,
    validPriceIds,
    onIncrement = () => { },
    onDecrement = () => { },
}) => {
    const { translate } = useTranslate();

    // `prices` is the union over the whole series; only these are bookable for
    // THIS occurrence's own date/time/weekday.
    const validPrices = useMemo(
        () => prices.filter((p) => validPriceIds.has(p.Id)),
        [prices, validPriceIds]
    );
    const hasPrices = prices.length > 0;
    const priceSign = hasPrices ? prices[0].PriceSign : '';
    // The selected row still prices from the full union — a valid selection is
    // always in it, and this keeps the display stable while a reselect settles.
    const rowPrice = useMemo(
        () => (prices.find((p) => p.Id === formValues.price)?.Price || 0) * formValues.quantity,
        [prices, formValues.price, formValues.quantity]
    );

    // Keep the selected category valid for THIS occurrence. Also preserves the
    // pre-existing "reselect on service change" behaviour.
    const prevServiceIdRef = React.useRef(service.Id);
    useEffect(() => {
        const isServiceChange = prevServiceIdRef.current !== service.Id;
        prevServiceIdRef.current = service.Id;
        if (validPrices.length === 0) return;
        const selectionIsValid = validPriceIds.has(formValues.price as number);
        if (!formValues.price || isServiceChange || !selectionIsValid) {
            change('price', validPrices[0].Id);
        }
    }, [validPrices, validPriceIds, service.Id, formValues.price, change]);

    const priceOptions = useMemo(
        () => prices.map((price: Price) => {
            const isValid = validPriceIds.has(price.Id);
            return {
                key: price.Id,
                value: price.Id,
                disabled: !isValid,
                text: isValid
                    ? price.PriceText
                    : `${price.PriceText} — ${translate('newBookingForm.prices.notAvailableForTime')}`,
            };
        }),
        [prices, validPriceIds, translate]
    );


    return (
        <Box className={cx('row')}>
            <Box className={cx('left', 'small')}>
                {hasPrices ? (
                    prices.length === 1 ? (
                        <Box className={cx('plain-price-text')}>
                            {prices[0].PriceText}
                            {validPrices.length === 0 ? (
                                <Box className={cx('price-unavailable')}>
                                    {translate('newBookingForm.prices.notAvailableForTime')}
                                </Box>
                            ) : null}
                        </Box>
                    ) : (
                        <Field
                            type="select"
                            name="price"
                            options={priceOptions}
                        />
                    )
                ) : null}
                <Box className={cx('counter-wrapper')}>
                    <span className="hidden-field">
                        <Field name="quantity" />
                    </span>
                    <Flex
                        justifyContent={'center'}
                        className={cx('counter')}
                        data-testid={`counter-${formValues.price}`}
                    >
                        <Counter
                            value={formValues.quantity}
                            // Counter only disables decrement when `min` is given,
                            // so without this the quantity goes negative.
                            min={0}
                            disabled={hasPrices && validPrices.length === 0}
                            onDecrement={() => {
                                onDecrement(formValues.quantity - 1, () => {
                                    change('quantity', formValues.quantity - 1);
                                });
                            }}
                            onIncrement={() => {
                                onIncrement(formValues.quantity + 1, () => {
                                    change('quantity', formValues.quantity + 1);
                                });
                            }}
                        />
                    </Flex>
                </Box>
            </Box>
            {hasPrices ? <Box className={cx('right')}>
                {rowPrice}
                {priceSign}
            </Box> : null}
        </Box>
    );
});
